import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn } from 'lucide-react';

/* ───── ZODIAC DATA ───── */
const ZODIAC = [
  { sym: '♈', el: 'fire' },   { sym: '♉', el: 'earth' },
  { sym: '♊', el: 'air' },    { sym: '♋', el: 'water' },
  { sym: '♌', el: 'fire' },   { sym: '♍', el: 'earth' },
  { sym: '♎', el: 'air' },    { sym: '♏', el: 'water' },
  { sym: '♐', el: 'fire' },   { sym: '♑', el: 'earth' },
  { sym: '♒', el: 'air' },    { sym: '♓', el: 'water' },
];

const EL_COLOR = {
  fire:  { bg: '#fff0f0', sym: '#c0392b' },
  earth: { bg: '#f0fff0', sym: '#27ae60' },
  air:   { bg: '#fffff0', sym: '#b8860b' },
  water: { bg: '#f0f0ff', sym: '#2563eb' },
};

/* ───── PLANET GLYPHS ───── */
const GLYPHS = {
  'Sol':      { g: '☉', c: '#D4A017' },
  'Luna':     { g: '☽', c: '#5a6e82' },
  'Mercurio': { g: '☿', c: '#B8860B' },
  'Venus':    { g: '♀', c: '#228B22' },
  'Marte':    { g: '♂', c: '#CD3333' },
  'Júpiter':  { g: '♃', c: '#6A5ACD' },
  'Jupiter':  { g: '♃', c: '#6A5ACD' },
  'Saturno':  { g: '♄', c: '#4682B4' },
  'Urano':    { g: '♅', c: '#20B2AA' },
  'Neptuno':  { g: '♆', c: '#4169E1' },
  'Plutón':   { g: '♇', c: '#8B008B' },
  'Pluton':   { g: '♇', c: '#8B008B' },
  'Nodo Norte': { g: '☊', c: '#2E8B57' },
  'Quirón':   { g: '⚷', c: '#8B4513' },
  'Quiron':   { g: '⚷', c: '#8B4513' },
  'Lilith':   { g: '⚸', c: '#4B0082' },
  'Parte de la Fortuna': { g: '⊕', c: '#DAA520' },
};

const EN2ES = {
  Sun: 'Sol', Moon: 'Luna', Mercury: 'Mercurio', Venus: 'Venus',
  Mars: 'Marte', Jupiter: 'Júpiter', Saturn: 'Saturno',
  Uranus: 'Urano', Neptune: 'Neptuno', Pluto: 'Plutón',
  True_North_Lunar_Node: 'Nodo Norte', Chiron: 'Quirón',
  Ascendant: 'Ascendente', Mean_Node: 'Nodo Norte',
  Mean_Lilith: 'Lilith',
  True_South_Lunar_Node: 'Nodo Sur', Descendant: 'Descendente',
  Medium_Coeli: 'Medio Cielo',
  Part_of_Fortune: 'Parte de la Fortuna',
};

function glyph(name) {
  if (GLYPHS[name]) return GLYPHS[name];
  const n = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  for (const [k, v] of Object.entries(GLYPHS)) {
    if (k.normalize('NFD').replace(/[\u0300-\u036f]/g, '') === n) return v;
  }
  return { g: name.slice(0, 2), c: '#666' };
}

/* ───── CHART DIMENSIONS ───── */
const W = 800, CX = 400, CY = 400;
const R_OUT    = 372;
const R_ZIN    = 333;
const R_TICK   = 333;
const R_HOUSE  = 260;
const R_PL_HI  = 314;   // outer tier
const R_PL     = 297;   // default planet ring
const R_PL_LO  = 278;   // inner tier
const R_DEG    = 260;   // degree label (just inside house ring)
const R_HNUM   = 195;   // house numbers
const R_ASP    = 170;   // aspect lines
const R_CTR    = 42;
const MIN_GAP  = 18;    // min angular separation (degrees) - larger for bigger glyphs

/* ───── COORDINATE HELPERS ───── */
function toA(deg, asc) { return ((180 - deg + asc) * Math.PI) / 180; }
function x(deg, r, asc) { return CX + r * Math.cos(toA(deg, asc)); }
function y(deg, r, asc) { return CY - r * Math.sin(toA(deg, asc)); }

/* ───── DE-CLUSTERING ALGORITHM ─────
   Spreads overlapping planets angularly, then assigns radial tiers
   for any remaining tight pairs. */
function deCluster(items) {
  const n = items.length;
  if (n === 0) return [];
  if (n === 1) return [{ ...items[0], dd: items[0].degree, tier: 0 }];

  // Sort by degree, keep original index
  const sorted = items
    .map((it, i) => ({ ...it, _i: i, _d: it.degree }))
    .sort((a, b) => a._d - b._d);

  // Phase 1: Angular relaxation (iterative pushing)
  for (let pass = 0; pass < 50; pass++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      let gap = sorted[j]._d - sorted[i]._d;
      if (gap < 0) gap += 360;
      if (gap > 0 && gap < MIN_GAP) {
        const push = (MIN_GAP - gap) / 2 + 0.25;
        sorted[i]._d = (sorted[i]._d - push + 360) % 360;
        sorted[j]._d = (sorted[j]._d + push) % 360;
        moved = true;
      }
    }
    if (!moved) break;
  }

  // Phase 2: Radial tier assignment for remaining close pairs
  const tiers = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    let gap = sorted[j]._d - sorted[i]._d;
    if (gap < 0) gap += 360;
    if (gap < MIN_GAP * 0.85) {
      tiers[j] = tiers[i] === 0 ? 1 : tiers[i] === 1 ? -1 : 0;
    }
  }

  return sorted.map((s, i) => ({
    ...items[s._i],
    dd: s._d,
    tier: tiers[i],
  }));
}

/* ───── ASPECT STYLING ───── */
const ASPECT_NAMES_ES = {
  conjunction: 'Conjuncion', trine: 'Trigono', sextile: 'Sextil',
  square: 'Cuadratura', opposition: 'Oposicion', quincunx: 'Quincuncio',
  quintile: 'Quintil', 'semi-sextile': 'Semi-sextil', 'semi-square': 'Semi-cuadratura',
  sesquiquadrate: 'Sesquicuadratura',
};
function aspectLabel(type) {
  const t = (type || '').toLowerCase();
  for (const [k, v] of Object.entries(ASPECT_NAMES_ES)) {
    if (t === k || t.includes(k)) return v;
  }
  return type || 'Aspecto';
}
function aspectStyle(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('conjun'))                     return { c: '#D4A017', w: 2, d: '' };
  if (t.includes('trine') || t.includes('tríg') || t.includes('trigono')) return { c: '#0000EE', w: 2.5, d: '' };
  if (t === 'semi-sextile')                     return { c: '#009900', w: 1.2, d: '5,3' };
  if (t === 'semi-square')                      return { c: '#CC6600', w: 1.5, d: '5,3' };
  if (t.includes('sextil'))                     return { c: '#0000EE', w: 1.8, d: '7,4' };
  if (t.includes('square') || t.includes('cuadr')) return { c: '#CC0000', w: 2.5, d: '' };
  if (t.includes('oppos'))                      return { c: '#880088', w: 2.8, d: '' };
  if (t.includes('quincunx') || t.includes('inconjun')) return { c: '#009900', w: 1.8, d: '' };
  if (t.includes('sesqui'))                     return { c: '#CC6600', w: 1.5, d: '' };
  if (t.includes('quintile'))                   return { c: '#666', w: 1, d: '3,3' };
  return { c: '#888', w: 1, d: '4,4' };
}

/* ───── MAIN COMPONENT ───── */
export default function NatalChartWheel({ chartData, aspects = [] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [aspectFilter, setAspectFilter] = useState('all'); // all, tension, harmonic, major
  const svgRef = useRef(null);
  const modalSvgRef = useRef(null);
  const pRaw = chartData?.planets || [];
  const asc = chartData?.ascendant_degree || 0;
  const hRaw = chartData?.houses || [];

  const svgContent = useMemo(() => {
    if (!pRaw.length) return '';

    const houses = hRaw.length === 12
      ? hRaw.map(h => h.abs_position)
      : Array.from({ length: 12 }, (_, i) => (asc + i * 30) % 360);

    const planetsFiltered = pRaw.filter(p => p.name !== 'Ascendente');
    const planets = deCluster(planetsFiltered);

    let s = '';

    /* 1. Background */
    s += `<circle cx="${CX}" cy="${CY}" r="${R_OUT}" fill="#faf9f4" stroke="#999" stroke-width="1.5"/>`;

    /* 2. Zodiac band - 12 segments */
    for (let i = 0; i < 12; i++) {
      const d1 = i * 30, d2 = (i + 1) * 30;
      const el = ZODIAC[i].el;
      const col = EL_COLOR[el];
      const x1o = x(d1, R_OUT, asc), y1o = y(d1, R_OUT, asc);
      const x2o = x(d2, R_OUT, asc), y2o = y(d2, R_OUT, asc);
      const x1i = x(d1, R_ZIN, asc), y1i = y(d1, R_ZIN, asc);
      const x2i = x(d2, R_ZIN, asc), y2i = y(d2, R_ZIN, asc);
      // Segment fill
      s += `<path d="M${x1o},${y1o}A${R_OUT},${R_OUT},0,0,1,${x2o},${y2o}L${x2i},${y2i}A${R_ZIN},${R_ZIN},0,0,0,${x1i},${y1i}Z" fill="${col.bg}" stroke="#bbb" stroke-width="0.5"/>`;
      // Divider line
      s += `<line x1="${x1o}" y1="${y1o}" x2="${x1i}" y2="${y1i}" stroke="#aaa" stroke-width="1"/>`;
      // Symbol
      const mx = x(d1 + 15, (R_OUT + R_ZIN) / 2, asc);
      const my = y(d1 + 15, (R_OUT + R_ZIN) / 2, asc);
      s += `<text x="${mx}" y="${my}" fill="${col.sym}" font-size="22" font-weight="bold" font-family="serif" text-anchor="middle" dominant-baseline="central">${ZODIAC[i].sym}</text>`;
    }

    /* 3. Degree ticks */
    s += `<circle cx="${CX}" cy="${CY}" r="${R_TICK}" fill="none" stroke="#bbb" stroke-width="0.6"/>`;
    for (let i = 0; i < 360; i++) {
      if (i % 30 === 0) continue;
      const len = i % 10 === 0 ? 9 : i % 5 === 0 ? 5 : 2.5;
      const sw = i % 10 === 0 ? 0.6 : 0.25;
      s += `<line x1="${x(i, R_TICK, asc)}" y1="${y(i, R_TICK, asc)}" x2="${x(i, R_TICK - len, asc)}" y2="${y(i, R_TICK - len, asc)}" stroke="#aaa" stroke-width="${sw}"/>`;
    }

    /* 4. House ring */
    s += `<circle cx="${CX}" cy="${CY}" r="${R_HOUSE}" fill="#fdfcf7" stroke="#bbb" stroke-width="0.8"/>`;

    /* 5. House cusp lines */
    houses.forEach((d, i) => {
      const angular = i === 0 || i === 3 || i === 6 || i === 9;
      s += `<line x1="${x(d, R_CTR, asc)}" y1="${y(d, R_CTR, asc)}" x2="${x(d, R_HOUSE, asc)}" y2="${y(d, R_HOUSE, asc)}" stroke="${angular ? '#444' : '#d0d0d0'}" stroke-width="${angular ? 2.5 : 0.5}"/>`;
    });

    /* 6. House numbers */
    houses.forEach((d, i) => {
      const nd = houses[(i + 1) % 12];
      let mid = (d + nd) / 2;
      if (nd < d) mid = ((d + nd + 360) / 2) % 360;
      s += `<text x="${x(mid, R_HNUM, asc)}" y="${y(mid, R_HNUM, asc)}" fill="#b0b0b0" font-size="16" font-family="sans-serif" text-anchor="middle" dominant-baseline="central">${i + 1}</text>`;
    });

    /* 7. Center circle */
    s += `<circle cx="${CX}" cy="${CY}" r="${R_CTR}" fill="#f5f4ee" stroke="#ccc" stroke-width="0.6"/>`;
    s += `<circle cx="${CX}" cy="${CY}" r="4" fill="#ddd"/>`;

    /* 8. Aspect lines - smart filtering + interactive data attrs */
    const norm = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');
    const findPl = (name) => {
      const search = norm(name);
      return pRaw.find(p => {
        const pn = norm(p.name);
        return pn === search || pn.includes(search) || search.includes(pn);
      });
    };

    const isHarmonic = t => /trine|sextil|semi-sextile/i.test(t);
    const isTension = t => /square|oppos|semi-square|sesqui/i.test(t);
    const isMajor = t => /conjun|trine|sextil|square|oppos/i.test(t) && !/semi/i.test(t);

    if (aspects?.length) {
      const drawable = [];
      aspects.forEach(a => {
        const n1 = EN2ES[a.planet1] || a.planet1;
        const n2 = EN2ES[a.planet2] || a.planet2;
        const p1 = findPl(n1), p2 = findPl(n2);
        if (!p1 || !p2) return;
        let angDiff = Math.abs(p1.degree - p2.degree);
        if (angDiff > 180) angDiff = 360 - angDiff;
        if (angDiff < 6) return;
        drawable.push({ ...a, _n1: n1, _n2: n2, _p1: p1, _p2: p2 });
      });
      drawable.sort((a, b) => (b.score || 0) - (a.score || 0));
      const TOP_WHEEL = 15;
      drawable.slice(0, TOP_WHEEL).forEach(a => {
        // Apply filter
        const t = a.aspect || '';
        if (aspectFilter === 'harmonic' && !isHarmonic(t) && !t.includes('conjun')) return;
        if (aspectFilter === 'tension' && !isTension(t)) return;
        if (aspectFilter === 'major' && !isMajor(t) && !t.includes('conjun')) return;

        const st = aspectStyle(a.aspect);
        const mvLabel = a.movement === 'Applying' ? 'Aplicando' : a.movement === 'Separating' ? 'Separando' : '';
        const label = `${a._n1} ${aspectLabel(a.aspect)} ${a._n2} (orbe: ${a.orbit}°${mvLabel ? ', ' + mvLabel : ''})`;
        const maxScore = drawable[0]?.score || 50;
        const opac = a.score ? Math.max(0.5, Math.min(0.95, 0.5 + 0.45 * (a.score / maxScore))) : 0.85;
        const pKey1 = norm(a._n1), pKey2 = norm(a._n2);
        s += `<g class="asp-line" data-asp-planets="${pKey1},${pKey2}" cursor="pointer"><title>${label}</title>`;
        s += `<line x1="${x(a._p1.degree, R_ASP, asc)}" y1="${y(a._p1.degree, R_ASP, asc)}" x2="${x(a._p2.degree, R_ASP, asc)}" y2="${y(a._p2.degree, R_ASP, asc)}" stroke="transparent" stroke-width="10"/>`;
        s += `<line x1="${x(a._p1.degree, R_ASP, asc)}" y1="${y(a._p1.degree, R_ASP, asc)}" x2="${x(a._p2.degree, R_ASP, asc)}" y2="${y(a._p2.degree, R_ASP, asc)}" stroke="${st.c}" stroke-width="${st.w}" ${st.d ? `stroke-dasharray="${st.d}"` : ''} opacity="${opac.toFixed(2)}" class="asp-visible"/>`;
        s += `</g>`;
      });
    }

    /* 9. AC / DC / MC / IC axis labels */
    const axDeg = [
      { d: asc, l: 'AC' },
      { d: (asc + 180) % 360, l: 'DC' },
      { d: houses[9] || (asc + 270) % 360, l: 'MC' },
      { d: houses[3] || (asc + 90) % 360, l: 'IC' },
    ];
    axDeg.forEach(ax => {
      const lx = x(ax.d, R_HOUSE + 16, asc), ly = y(ax.d, R_HOUSE + 16, asc);
      s += `<text x="${lx}" y="${ly}" fill="#222" font-size="14" font-weight="bold" font-family="sans-serif" text-anchor="middle" dominant-baseline="central" stroke="#faf9f4" stroke-width="5" paint-order="stroke">${ax.l}</text>`;
    });

    /* 10. Planets - with data-planet for hover interactivity */
    planets.forEach(p => {
      const real = p.degree;
      const show = p.dd;
      const gl = glyph(p.name);
      const signDeg = Math.floor(real % 30);
      const signMin = Math.floor((real % 1) * 60);
      const tierR = p.tier === 0 ? R_PL : p.tier === 1 ? R_PL_HI : R_PL_LO;
      const degR = p.tier === 0 ? R_DEG : p.tier === 1 ? R_DEG + 17 : R_DEG - 13;
      const pKey = norm(p.name);

      s += `<g class="planet-g" data-planet="${pKey}" cursor="pointer">`;

      // Connector: tick on house ring → displayed position
      const tx1 = x(real, R_HOUSE, asc), ty1 = y(real, R_HOUSE, asc);
      const tx2 = x(real, R_HOUSE - 5, asc), ty2 = y(real, R_HOUSE - 5, asc);
      s += `<line x1="${tx1}" y1="${ty1}" x2="${tx2}" y2="${ty2}" stroke="${gl.c}" stroke-width="1.5"/>`;

      const sx = x(show, tierR, asc), sy = y(show, tierR, asc);
      s += `<line x1="${tx1}" y1="${ty1}" x2="${sx}" y2="${sy}" stroke="${gl.c}" stroke-width="0.5" opacity="0.45"/>`;

      // Planet symbol with halo
      s += `<text x="${sx}" y="${sy}" fill="${gl.c}" font-size="26" font-weight="bold" font-family="DejaVu Sans,sans-serif" text-anchor="middle" dominant-baseline="central" stroke="#faf9f4" stroke-width="6" paint-order="stroke">${gl.g}</text>`;

      // Degree label
      const dx = x(show, degR, asc), dy = y(show, degR, asc);
      s += `<text x="${dx}" y="${dy}" fill="${gl.c}" font-size="13" font-weight="700" font-family="sans-serif" text-anchor="middle" dominant-baseline="central" stroke="#faf9f4" stroke-width="4" paint-order="stroke">${signDeg}°${String(signMin).padStart(2, '0')}'</text>`;

      // Retrograde marker
      if (p.retrograde) {
        s += `<text x="${sx + 16}" y="${sy - 14}" fill="#CC0000" font-size="12" font-weight="bold" font-family="sans-serif" stroke="#faf9f4" stroke-width="3" paint-order="stroke">R</text>`;
      }

      s += `</g>`; // close planet-g
    });

    return s;
  }, [pRaw, asc, hRaw, aspects, aspectFilter]);

  /* Count actually drawn aspects for legend */
  const aspectCounts = useMemo(() => {
    if (!aspects?.length || !pRaw.length) return null;
    const norm = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');
    const findPl = (name) => pRaw.find(p => {
      const pn = norm(p.name); const s = norm(name);
      return pn === s || pn.includes(s) || s.includes(pn);
    });
    // Same logic as SVG: both drawable + top 15
    const drawable = [];
    aspects.forEach(a => {
      const n1 = EN2ES[a.planet1] || a.planet1;
      const n2 = EN2ES[a.planet2] || a.planet2;
      if (!findPl(n1) || !findPl(n2)) return;
      const p1 = findPl(n1), p2 = findPl(n2);
      let diff = Math.abs(p1.degree - p2.degree);
      if (diff > 180) diff = 360 - diff;
      if (diff < 6) return;
      drawable.push(a);
    });
    drawable.sort((a, b) => (b.score || 0) - (a.score || 0));
    const counts = { trine: 0, sextile: 0, square: 0, opposition: 0, conjunction: 0, quincunx: 0, semisextile: 0, semisquare: 0, sesqui: 0, other: 0 };
    drawable.slice(0, 15).forEach(a => {
      const t = (a.aspect || '').toLowerCase();
      if (t.includes('conjun')) counts.conjunction++;
      else if (t.includes('trine') || t.includes('tríg') || t.includes('trigono')) counts.trine++;
      else if (t === 'semi-sextile') counts.semisextile++;
      else if (t === 'semi-square') counts.semisquare++;
      else if (t.includes('sextil')) counts.sextile++;
      else if (t.includes('square') || t.includes('cuadr')) counts.square++;
      else if (t.includes('oppos')) counts.opposition++;
      else if (t.includes('quincunx') || t.includes('inconjun')) counts.quincunx++;
      else if (t.includes('sesqui')) counts.sesqui++;
      else counts.other++;
    });
    return counts;
  }, [aspects, pRaw]);

  /* ── Hover interactivity: highlight planet + its aspects ── */
  const attachHover = useCallback((container) => {
    if (!container) return;
    const svg = container.querySelector('svg');
    if (!svg) return;

    const allPlanets = svg.querySelectorAll('.planet-g');
    const allAspects = svg.querySelectorAll('.asp-line');

    const reset = () => {
      allPlanets.forEach(g => { g.style.opacity = ''; g.style.filter = ''; });
      allAspects.forEach(g => {
        g.style.opacity = '';
        const vis = g.querySelector('.asp-visible');
        if (vis) vis.style.strokeWidth = '';
      });
    };

    allPlanets.forEach(pg => {
      const pKey = pg.getAttribute('data-planet');
      pg.addEventListener('mouseenter', () => {
        allPlanets.forEach(g => { g.style.opacity = g === pg ? '1' : '0.25'; });
        allAspects.forEach(g => {
          const keys = (g.getAttribute('data-asp-planets') || '').split(',');
          const match = keys.includes(pKey);
          g.style.opacity = match ? '1' : '0.08';
          const vis = g.querySelector('.asp-visible');
          if (vis && match) vis.style.strokeWidth = String(parseFloat(vis.getAttribute('stroke-width') || 2) * 1.6);
        });
      });
      pg.addEventListener('mouseleave', reset);
    });

    return reset;
  }, []);

  useEffect(() => {
    const cleanup1 = attachHover(svgRef.current);
    return () => { if (cleanup1) cleanup1(); };
  }, [svgContent, attachHover]);

  useEffect(() => {
    if (modalOpen) {
      const timer = setTimeout(() => attachHover(modalSvgRef.current), 100);
      return () => clearTimeout(timer);
    }
  }, [modalOpen, svgContent, attachHover]);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
      const handler = (e) => { if (e.key === 'Escape') setModalOpen(false); };
      document.addEventListener('keydown', handler);
      return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
    }
  }, [modalOpen]);

  if (!pRaw.length) return null;

  const fullSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" width="100%" style="border-radius:12px;background:#fdfcf7">${svgContent}</svg>`;

  const modal = modalOpen ? createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(11,14,26,0.95)', backdropFilter: 'blur(8px)' }} onClick={() => setModalOpen(false)} data-testid="natal-chart-modal">
      <button style={{ position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999, fontSize: 14, fontWeight: 500, background: 'rgba(20,24,47,0.9)', color: '#F5F5F7', border: '1px solid rgba(230,195,106,0.2)', cursor: 'pointer' }} onClick={() => setModalOpen(false)} data-testid="natal-chart-modal-close"><X size={16} /> Cerrar</button>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(92vw, 92vh)', height: 'min(92vw, 92vh)', padding: 8 }}>
        <div ref={modalSvgRef} dangerouslySetInnerHTML={{ __html: fullSvg }} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>, document.body
  ) : null;

  const FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'major', label: 'Mayores' },
    { key: 'harmonic', label: 'Armonicos' },
    { key: 'tension', label: 'Tensiones' },
  ];

  return (
    <>
      <div style={{ maxWidth: 600, margin: '0 auto', position: 'relative' }} data-testid="natal-chart-wheel">
        {/* Filter toggles */}
        <div className="flex justify-center gap-1.5 mb-2" data-testid="aspect-filter-bar">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={(e) => { e.stopPropagation(); setAspectFilter(f.key); }}
              className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all ${
                aspectFilter === f.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-black/30 text-[var(--text-muted)] hover:bg-black/50'
              }`}
              style={{ border: aspectFilter === f.key ? '1px solid #7c3aed' : '1px solid rgba(255,255,255,0.08)' }}
              data-testid={`filter-${f.key}`}
            >{f.label}</button>
          ))}
        </div>
        <div ref={svgRef} dangerouslySetInnerHTML={{ __html: fullSvg }} style={{ cursor: 'pointer' }} onClick={() => setModalOpen(true)} />
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'rgba(20,24,47,0.85)', color: '#E6C36A', backdropFilter: 'blur(8px)', border: '1px solid rgba(230,195,106,0.2)' }} data-testid="chart-zoom-hint">
          <ZoomIn className="w-3.5 h-3.5" /> Ver completo
        </div>
      </div>
      {/* Aspect Legend */}
      {aspectCounts && (
        <div className="mt-4 max-w-md mx-auto" data-testid="aspect-legend">
          <p className="text-xs text-center text-[var(--text-muted)] mb-2 font-semibold uppercase tracking-wider">Aspectos en el grafico</p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5">
            {aspectCounts.conjunction > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#D4A017" strokeWidth="2.5"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Conjuncion ({aspectCounts.conjunction})</span>
              </div>
            )}
            {aspectCounts.trine > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#0000EE" strokeWidth="2.5"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Trigono ({aspectCounts.trine})</span>
              </div>
            )}
            {aspectCounts.sextile > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#0000EE" strokeWidth="2" strokeDasharray="5,3"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Sextil ({aspectCounts.sextile})</span>
              </div>
            )}
            {aspectCounts.square > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#CC0000" strokeWidth="2.5"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Cuadratura ({aspectCounts.square})</span>
              </div>
            )}
            {aspectCounts.opposition > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#880088" strokeWidth="3"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Oposicion ({aspectCounts.opposition})</span>
              </div>
            )}
            {aspectCounts.quincunx > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#009900" strokeWidth="2"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Quincuncio ({aspectCounts.quincunx})</span>
              </div>
            )}
            {aspectCounts.semisextile > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#009900" strokeWidth="1.5" strokeDasharray="5,3"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Semi-sextil ({aspectCounts.semisextile})</span>
              </div>
            )}
            {aspectCounts.semisquare > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#CC6600" strokeWidth="1.5" strokeDasharray="5,3"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Semi-cuadratura ({aspectCounts.semisquare})</span>
              </div>
            )}
            {aspectCounts.sesqui > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#CC6600" strokeWidth="1.5"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Sesquicuadratura ({aspectCounts.sesqui})</span>
              </div>
            )}
            {aspectCounts.other > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#666" strokeWidth="1.5" strokeDasharray="3,3"/></svg>
                <span className="text-xs text-[var(--text-secondary)]">Menor ({aspectCounts.other})</span>
              </div>
            )}
          </div>
        </div>
      )}
      {modal}
    </>
  );
}
